import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { qrcodeAPIService } from '~/api/qrcode';
import { BackdropFullScreen } from '~/components/atoms/loader/BackdropFullScreen';
import { TextMockup } from '~/components/atoms/mockup-previews/TextMockup';
import { ConfirmNavigateModal } from '~/components/organisms/ConfirmNavigateModal';
import { QR_CODE_FOLDER_FORM_NAME, QRCODE_STATIC_STEP, QRCODE_STATIC_TYPE } from '~/constants/qr-code';
import { PATH } from '~/constants/route-path';
import useQrCodeData from '~/hooks/useQrCodeData';
import { useZodForm } from '~/hooks/useZodForm';
import { StaticQrContentLayout } from '~/pages/qrBuilder/p-static/components/static-qr-content-layout/StaticQrContentLayout';
import { StaticQrCodeData } from '~/types/qrCode';
import { mapQrTypeForApi } from '~/utils/qr-code';

import { StaticTextForm } from './components/Form';
import { getSchema } from './utils/form-schema';

export const EditStaticTextContent = () => {
  const navigate = useNavigate();
  const form = useZodForm({ getSchema });
  const location = useLocation();
  const [{ params }] = matchRoutes([{ path: PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.CONTENT) }], location);
  const text = form.watch('text');

  const { data: qrCode, isLoading: qrCodeIsLoading } = useQrCodeData<StaticQrCodeData>({ qrCodeId: params.id });

  const isChangeQrType = params.type !== qrCode?.content.type;

  const changeStaticQrTypeMutation = useMutation({ mutationFn: qrcodeAPIService.changeStaticQrType });
  const updateStaticQrTextMutation = useMutation({ mutationFn: qrcodeAPIService.updateStaticQrText });

  const isNextProcessing = changeStaticQrTypeMutation.isPending || updateStaticQrTextMutation.isPending;

  useEffect(() => {
    if (qrCode) {
      if (!isChangeQrType) {
        form.setValue('text', qrCode.content.text);
      }

      form.setValue('name', qrCode.name);
      // form.setValue('passwordProtection.isPasswordProtected', qrCode.isPasswordProtected ?? false);

      if (qrCode.folder) {
        form.setValue(QR_CODE_FOLDER_FORM_NAME, qrCode.folder.id);
      }
    }
  }, [qrCode?.id]);

  const onSubmit = (values) => {
    if (isNextProcessing) return;

    const payload = {
      ...(isChangeQrType ? { type: mapQrTypeForApi(params.type) } : {}),
      id: params.id,
      body: {
        name: values.name,
        content: { text: values.text },
        ...(values.qrCodeFolder ? { folder_id: values.qrCodeFolder } : {}),
        // isPasswordProtected: values.passwordProtection.isPasswordProtected,
        // password: values.passwordProtection.password,
      },
    };

    const mutation = isChangeQrType ? changeStaticQrTypeMutation : updateStaticQrTextMutation;

    mutation.mutate(payload, {
      onSuccess: () => {
        navigate(PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.STYLES, QRCODE_STATIC_TYPE.TEXT, params.id));
      },
    });
  };

  const handleClickStep = async (step) => {
    if (step === 0) navigate(PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.TYPE, QRCODE_STATIC_TYPE.TEXT, params.id));
    if (step === 2) await form.handleSubmit(onSubmit)();
  };

  return (
    <FormProvider {...form}>
      <StaticQrContentLayout
        handleStep={handleClickStep}
        isNextProcessing={isNextProcessing}
        activeStep={1}
        qrCode={qrCode}
        mockup={<TextMockup message={text} />}
        form={
          <BackdropFullScreen isLoading={qrCodeIsLoading}>
            <StaticTextForm />
          </BackdropFullScreen>
        }
      />

      <ConfirmNavigateModal activeStep={1} />
    </FormProvider>
  );
};
